import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  DiamondOutlined as DiamondIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import AdminChrome, {
  adminOutlineBtnSx,
  adminSectionTitleSx,
  adminSolidBtnSx,
  adminStatPaperSx,
  adminSurfaceCardSx,
} from '../components/AdminChrome';
import { DOCUMENT } from '../constants/documentTheme';
import { documentSubTabsSx } from '../constants/documentTheme';
import {
  INQUIRY_STAGES,
  PRIVATE_NAV,
  REGIONS,
  SOURCES,
  destinationLabel,
  formatBudget,
  privateNavValue,
} from './adminPrivate/privateTravelConstants';
import PrivateProposalsPanel from './adminPrivate/PrivateProposalsPanel';
import PrivatePartnersPanel from './adminPrivate/PrivatePartnersPanel';
import PrivatePerformancePanel from './adminPrivate/PrivatePerformancePanel';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    bgcolor: DOCUMENT.ivory,
  },
  '& .MuiInputLabel-root': {
    fontFamily: DOCUMENT.sans,
  },
};

const selectMenuProps = {
  PaperProps: {
    sx: {
      borderRadius: 0,
      border: `1px solid ${DOCUMENT.rule}`,
      bgcolor: DOCUMENT.canvas,
      maxHeight: 240,
      fontFamily: DOCUMENT.sans,
    },
  },
};

const EMPTY_INQUIRY = {
  clientName: '',
  clientOrganization: '',
  clientEmail: '',
  clientPhone: '',
  source: 'direct',
  advisorOwnedClient: false,
  groupType: '',
  partySize: '',
  destinations: '',
  dateStart: '',
  dateEnd: '',
  budgetMin: '',
  budgetMax: '',
  budgetCurrency: 'USD',
  occasion: '',
  urgency: '',
  nextAction: 'Qualify inquiry',
  assignedOwnerLabel: '',
  leadflowLeadId: '',
  consentContact: false,
};

const EMPTY_SUPPLIER = {
  businessName: '',
  destination: '',
  destinationRegion: 'other',
  publicUrl: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  verificationStatus: 'prospect',
  paymentCollector: 'supplier',
  groupSizeSupport: '',
  contractStatus: '',
  permissionToPublish: false,
  internalNotes: '',
};

function EmptyBlock({ title, body, action }) {
  return (
    <Box sx={{ py: 8, px: 3, textAlign: 'center' }} data-testid="private-empty-state">
      <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 24, mb: 1, color: DOCUMENT.ink }}>
        {title}
      </Typography>
      {body ? (
        <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2, maxWidth: '48ch', mx: 'auto', mb: action ? 2.5 : 0, lineHeight: 1.65 }}>
          {body}
        </Typography>
      ) : null}
      {action}
    </Box>
  );
}

function StageChip({ stage, t }) {
  return (
    <Chip
      size="small"
      label={t(`adminPrivate.stages.${stage}`, { defaultValue: stage })}
      sx={{
        borderRadius: 0,
        bgcolor: DOCUMENT.ivory,
        border: `1px solid ${DOCUMENT.rule}`,
        fontFamily: DOCUMENT.sans,
        fontSize: 11,
        letterSpacing: '0.04em',
      }}
    />
  );
}

function PrivateSubNav() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const current = privateNavValue(pathname);

  return (
    <Tabs
      value={current}
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
      sx={{
        ...documentSubTabsSx,
        mb: 3,
        minHeight: 44,
        borderBottom: `1px solid ${DOCUMENT.rule}`,
      }}
    >
      {PRIVATE_NAV.map((item) => (
        <Tab
          key={item.key}
          label={t(item.labelKey)}
          value={item.path}
          component={RouterLink}
          to={item.path}
          disableRipple
        />
      ))}
    </Tabs>
  );
}

function OverviewPanel({ overview, loading, onRefresh, t, navigate }) {
  if (loading && !overview) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} sx={{ color: DOCUMENT.oxblood }} />
      </Box>
    );
  }

  const kpis = overview?.kpis || {};
  const pipeline = overview?.pipeline || [];
  const actions = overview?.actionRequired || [];
  const recent = overview?.recentInquiries || [];
  const readiness = overview?.supplierReadiness || {};

  const statCards = [
    { key: 'new', label: t('adminPrivate.kpi.new'), value: kpis.newInquiries ?? 0 },
    { key: 'qualified', label: t('adminPrivate.kpi.qualified'), value: kpis.qualified ?? 0 },
    { key: 'proposals', label: t('adminPrivate.kpi.proposalsSent'), value: kpis.proposalsSent ?? 0 },
    { key: 'confirmed', label: t('adminPrivate.kpi.confirmed'), value: kpis.confirmedBookings ?? 0 },
    {
      key: 'gmv',
      label: t('adminPrivate.kpi.gmv'),
      value: kpis.grossBookingValueDisplay || t('adminPrivate.notVerified'),
    },
    {
      key: 'revenue',
      label: t('adminPrivate.kpi.dvnRevenue'),
      value: kpis.dvnEarnedRevenueDisplay || t('adminPrivate.notVerified'),
    },
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button startIcon={<RefreshIcon />} onClick={onRefresh} sx={adminOutlineBtnSx}>
          {t('adminPrivate.refresh')}
        </Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((card) => (
          <Grid item xs={6} md={4} lg={2} key={card.key}>
            <Card sx={adminStatPaperSx} elevation={0}>
              <Typography sx={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: DOCUMENT.ink3, mb: 1 }}>
                {card.label}
              </Typography>
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: '1.5rem', color: DOCUMENT.ink }}>
                {card.value}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={7}>
          <Card sx={{ ...adminSurfaceCardSx, p: 2.5 }} elevation={0}>
            <Typography sx={adminSectionTitleSx}>{t('adminPrivate.pipelineTitle')}</Typography>
            <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13, mb: 2 }}>
              {t('adminPrivate.pipelineHint')}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {pipeline.map((step, idx) => (
                <Box key={step.stage} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={`${t(`adminPrivate.stages.${step.stage}`)} · ${step.count}`}
                    sx={{
                      borderRadius: 0,
                      bgcolor: DOCUMENT.ivory,
                      border: `1px solid ${DOCUMENT.ruleHard}`,
                      fontFamily: DOCUMENT.sans,
                    }}
                  />
                  {idx < pipeline.length - 1 ? (
                    <Typography sx={{ color: DOCUMENT.ink3, fontSize: 12 }}>→</Typography>
                  ) : null}
                </Box>
              ))}
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card sx={{ ...adminSurfaceCardSx, p: 2.5, height: '100%' }} elevation={0}>
            <Typography sx={adminSectionTitleSx}>{t('adminPrivate.actionRequired')}</Typography>
            {actions.length === 0 ? (
              <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13, mt: 1 }}>
                {t('adminPrivate.noActions')}
              </Typography>
            ) : (
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                {actions.map((item) => (
                  <Box
                    key={item.inquiryId}
                    component={RouterLink}
                    to={`/admin/private/inquiries/${item.inquiryId}`}
                    sx={{ textDecoration: 'none', color: 'inherit', borderBottom: `1px solid ${DOCUMENT.rule}`, pb: 1 }}
                  >
                    <Typography sx={{ fontSize: 14, color: DOCUMENT.ink }}>{item.clientName}</Typography>
                    <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3 }}>
                      {item.nextAction}
                      {item.owner ? ` · ${item.owner}` : ''}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ ...adminSurfaceCardSx, mb: 3 }} elevation={0}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography sx={adminSectionTitleSx}>{t('adminPrivate.recentInquiries')}</Typography>
            <Button component={RouterLink} to="/admin/private/inquiries" sx={adminOutlineBtnSx}>
              {t('adminPrivate.viewAll')}
            </Button>
          </Stack>
          {recent.length === 0 ? (
            <EmptyBlock
              title={t('adminPrivate.emptyInquiriesTitle')}
              body={t('adminPrivate.emptyInquiriesBody')}
              action={(
                <Button
                  startIcon={<AddIcon />}
                  sx={adminSolidBtnSx}
                  onClick={() => navigate('/admin/private/inquiries?new=1')}
                >
                  {t('adminPrivate.newInquiry')}
                </Button>
              )}
            />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('adminPrivate.cols.client')}</TableCell>
                    <TableCell>{t('adminPrivate.cols.destination')}</TableCell>
                    <TableCell>{t('adminPrivate.cols.dates')}</TableCell>
                    <TableCell>{t('adminPrivate.cols.budget')}</TableCell>
                    <TableCell>{t('adminPrivate.cols.stage')}</TableCell>
                    <TableCell>{t('adminPrivate.cols.owner')}</TableCell>
                    <TableCell>{t('adminPrivate.cols.next')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recent.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/admin/private/inquiries/${row.id}`)}
                    >
                      <TableCell>{row.clientName}</TableCell>
                      <TableCell>{destinationLabel(row)}</TableCell>
                      <TableCell>
                        {[row.dateStart, row.dateEnd].filter(Boolean).join(' → ') || '—'}
                      </TableCell>
                      <TableCell>{formatBudget(row)}</TableCell>
                      <TableCell><StageChip stage={row.stage} t={t} /></TableCell>
                      <TableCell>{row.assignedOwnerLabel || '—'}</TableCell>
                      <TableCell>{row.nextAction || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Card sx={adminSurfaceCardSx} elevation={0}>
        <CardContent>
          <Typography sx={adminSectionTitleSx}>{t('adminPrivate.supplierReadiness')}</Typography>
          <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13, mb: 2 }}>
            {t('adminPrivate.supplierReadinessHint', {
              verified: readiness.verified ?? 0,
              awaiting: readiness.awaiting ?? 0,
            })}
          </Typography>
          <Grid container spacing={2}>
            {['italy', 'morocco', 'japan'].map((region) => {
              const data = readiness.byRegion?.[region] || { verified: 0, awaiting: 0, total: 0 };
              return (
                <Grid item xs={12} sm={4} key={region}>
                  <Box sx={{ p: 2, border: `1px solid ${DOCUMENT.rule}`, bgcolor: DOCUMENT.ivory }}>
                    <Typography sx={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: DOCUMENT.accent, mb: 1 }}>
                      {t(`adminPrivate.regions.${region}`)}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: DOCUMENT.ink }}>
                      {t('adminPrivate.supplierRegionStats', {
                        verified: data.verified,
                        awaiting: data.awaiting,
                      })}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}

function InquiriesPanel({ t, navigate, openNewOnMount }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inquiries, setInquiries] = useState([]);
  const [stageFilter, setStageFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(Boolean(openNewOnMount));
  const [draft, setDraft] = useState(EMPTY_INQUIRY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (stageFilter) params.stage = stageFilter;
      const { data } = await api.get('/admin/private/inquiries', { params });
      setInquiries(data.inquiries || []);
    } catch (err) {
      setError(err.response?.data?.error || t('adminPrivate.loadError'));
    } finally {
      setLoading(false);
    }
  }, [stageFilter, t]);

  useEffect(() => {
    load();
  }, [load]);

  const saveInquiry = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...draft,
        partySize: draft.partySize === '' ? null : Number(draft.partySize),
        budgetMin: draft.budgetMin === '' ? null : Number(draft.budgetMin),
        budgetMax: draft.budgetMax === '' ? null : Number(draft.budgetMax),
        leadflowLeadId: draft.leadflowLeadId === '' ? null : Number(draft.leadflowLeadId),
        destinations: String(draft.destinations || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      };
      const { data } = await api.post('/admin/private/inquiries', payload);
      setDialogOpen(false);
      setDraft(EMPTY_INQUIRY);
      navigate(`/admin/private/inquiries/${data.inquiry.id}`);
    } catch (err) {
      setError(err.response?.data?.error || t('adminPrivate.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }} onClose={() => setError('')}>{error}</Alert> : null}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180, ...fieldSx }}>
          <InputLabel>{t('adminPrivate.filterStage')}</InputLabel>
          <Select
            label={t('adminPrivate.filterStage')}
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            MenuProps={selectMenuProps}
          >
            <MenuItem value="">{t('adminPrivate.allStages')}</MenuItem>
            {INQUIRY_STAGES.map((stage) => (
              <MenuItem key={stage} value={stage}>{t(`adminPrivate.stages.${stage}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          startIcon={<AddIcon />}
          sx={adminSolidBtnSx}
          onClick={() => {
            setDraft(EMPTY_INQUIRY);
            setDialogOpen(true);
          }}
        >
          {t('adminPrivate.newInquiry')}
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} sx={{ color: DOCUMENT.oxblood }} />
        </Box>
      ) : inquiries.length === 0 ? (
        <EmptyBlock
          title={t('adminPrivate.emptyInquiriesTitle')}
          body={t('adminPrivate.emptyInquiriesBody')}
          action={(
            <Button startIcon={<AddIcon />} sx={adminSolidBtnSx} onClick={() => setDialogOpen(true)}>
              {t('adminPrivate.newInquiry')}
            </Button>
          )}
        />
      ) : (
        <TableContainer sx={{ ...adminSurfaceCardSx }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('adminPrivate.cols.client')}</TableCell>
                <TableCell>{t('adminPrivate.cols.source')}</TableCell>
                <TableCell>{t('adminPrivate.cols.destination')}</TableCell>
                <TableCell>{t('adminPrivate.cols.stage')}</TableCell>
                <TableCell>{t('adminPrivate.cols.owner')}</TableCell>
                <TableCell>{t('adminPrivate.cols.next')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inquiries.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/admin/private/inquiries/${row.id}`)}
                >
                  <TableCell>
                    <Typography sx={{ fontSize: 14 }}>{row.clientName}</Typography>
                    {row.advisorOwnedClient ? (
                      <Typography sx={{ fontSize: 11, color: DOCUMENT.accent }}>
                        {t('adminPrivate.advisorOwned')}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{t(`adminPrivate.sources.${row.source}`)}</TableCell>
                  <TableCell>{destinationLabel(row)}</TableCell>
                  <TableCell><StageChip stage={row.stage} t={t} /></TableCell>
                  <TableCell>{row.assignedOwnerLabel || '—'}</TableCell>
                  <TableCell>{row.nextAction || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 0, border: `1px solid ${DOCUMENT.rule}` } }}>
        <DialogTitle sx={{ fontFamily: DOCUMENT.display }}>{t('adminPrivate.newInquiry')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label={t('adminPrivate.fields.clientName')} required value={draft.clientName} onChange={(e) => setDraft((d) => ({ ...d, clientName: e.target.value }))} sx={fieldSx} fullWidth />
            <TextField label={t('adminPrivate.fields.organization')} value={draft.clientOrganization} onChange={(e) => setDraft((d) => ({ ...d, clientOrganization: e.target.value }))} sx={fieldSx} fullWidth />
            <TextField label={t('adminPrivate.fields.email')} value={draft.clientEmail} onChange={(e) => setDraft((d) => ({ ...d, clientEmail: e.target.value }))} sx={fieldSx} fullWidth />
            <FormControl fullWidth sx={fieldSx}>
              <InputLabel>{t('adminPrivate.fields.source')}</InputLabel>
              <Select label={t('adminPrivate.fields.source')} value={draft.source} onChange={(e) => setDraft((d) => ({ ...d, source: e.target.value }))} MenuProps={selectMenuProps}>
                {SOURCES.map((s) => <MenuItem key={s} value={s}>{t(`adminPrivate.sources.${s}`)}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={draft.advisorOwnedClient} onChange={(e) => setDraft((d) => ({ ...d, advisorOwnedClient: e.target.checked }))} />}
              label={t('adminPrivate.fields.advisorOwned')}
            />
            <TextField label={t('adminPrivate.fields.destinations')} helperText={t('adminPrivate.fields.destinationsHint')} value={draft.destinations} onChange={(e) => setDraft((d) => ({ ...d, destinations: e.target.value }))} sx={fieldSx} fullWidth />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField type="date" label={t('adminPrivate.fields.dateStart')} InputLabelProps={{ shrink: true }} value={draft.dateStart} onChange={(e) => setDraft((d) => ({ ...d, dateStart: e.target.value }))} sx={fieldSx} fullWidth />
              <TextField type="date" label={t('adminPrivate.fields.dateEnd')} InputLabelProps={{ shrink: true }} value={draft.dateEnd} onChange={(e) => setDraft((d) => ({ ...d, dateEnd: e.target.value }))} sx={fieldSx} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label={t('adminPrivate.fields.budgetMin')} value={draft.budgetMin} onChange={(e) => setDraft((d) => ({ ...d, budgetMin: e.target.value }))} sx={fieldSx} fullWidth />
              <TextField label={t('adminPrivate.fields.budgetMax')} value={draft.budgetMax} onChange={(e) => setDraft((d) => ({ ...d, budgetMax: e.target.value }))} sx={fieldSx} fullWidth />
            </Stack>
            <TextField label={t('adminPrivate.fields.leadflowId')} helperText={t('adminPrivate.fields.leadflowHint')} value={draft.leadflowLeadId} onChange={(e) => setDraft((d) => ({ ...d, leadflowLeadId: e.target.value }))} sx={fieldSx} fullWidth />
            <TextField label={t('adminPrivate.fields.nextAction')} value={draft.nextAction} onChange={(e) => setDraft((d) => ({ ...d, nextAction: e.target.value }))} sx={fieldSx} fullWidth />
            <FormControlLabel
              control={<Switch checked={draft.consentContact} onChange={(e) => setDraft((d) => ({ ...d, consentContact: e.target.checked }))} />}
              label={t('adminPrivate.fields.consent')}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={adminOutlineBtnSx}>{t('common.cancel')}</Button>
          <Button onClick={saveInquiry} disabled={saving || !draft.clientName.trim()} sx={adminSolidBtnSx}>
            {saving ? t('adminPrivate.saving') : t('adminPrivate.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function InquiryDetailPanel({ t, navigate }) {
  const { inquiryId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [inquiry, setInquiry] = useState(null);
  const [activity, setActivity] = useState([]);
  const [stage, setStage] = useState('new');
  const [nextAction, setNextAction] = useState('');
  const [leadflowLeadId, setLeadflowLeadId] = useState('');
  const [owner, setOwner] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/admin/private/inquiries/${inquiryId}`);
      setInquiry(data.inquiry);
      setActivity(data.activity || []);
      setStage(data.inquiry.stage);
      setNextAction(data.inquiry.nextAction || '');
      setLeadflowLeadId(data.inquiry.leadflowLeadId != null ? String(data.inquiry.leadflowLeadId) : '');
      setOwner(data.inquiry.assignedOwnerLabel || '');
    } catch (err) {
      setError(err.response?.data?.error || t('adminPrivate.loadError'));
    } finally {
      setLoading(false);
    }
  }, [inquiryId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = async (body, successKey) => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const { data } = await api.patch(`/admin/private/inquiries/${inquiryId}`, body);
      setInquiry(data.inquiry);
      setActivity(data.activity || []);
      setStage(data.inquiry.stage);
      setNextAction(data.inquiry.nextAction || '');
      setLeadflowLeadId(data.inquiry.leadflowLeadId != null ? String(data.inquiry.leadflowLeadId) : '');
      setOwner(data.inquiry.assignedOwnerLabel || '');
      setNotice(t(successKey));
    } catch (err) {
      setError(err.response?.data?.error || t('adminPrivate.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} sx={{ color: DOCUMENT.oxblood }} />
      </Box>
    );
  }

  if (!inquiry) {
    return (
      <EmptyBlock
        title={t('adminPrivate.inquiryNotFound')}
        action={<Button sx={adminOutlineBtnSx} onClick={() => navigate('/admin/private/inquiries')}>{t('adminPrivate.backToInquiries')}</Button>}
      />
    );
  }

  return (
    <Box>
      <Button sx={{ ...adminOutlineBtnSx, mb: 2 }} onClick={() => navigate('/admin/private/inquiries')}>
        {t('adminPrivate.backToInquiries')}
      </Button>
      {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }}>{error}</Alert> : null}
      {notice ? <Alert severity="success" sx={{ mb: 2, borderRadius: 0 }}>{notice}</Alert> : null}

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card sx={{ ...adminSurfaceCardSx, p: 2.5 }} elevation={0}>
            <Typography sx={adminSectionTitleSx}>{inquiry.clientName}</Typography>
            <Typography sx={{ color: DOCUMENT.ink3, mb: 2, fontSize: 13 }}>
              {inquiry.clientOrganization || t('adminPrivate.noOrganization')}
              {inquiry.advisorOwnedClient ? ` · ${t('adminPrivate.advisorOwned')}` : ''}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>{t('adminPrivate.cols.destination')}</Typography>
                <Typography sx={{ mb: 1.5 }}>{destinationLabel(inquiry)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>{t('adminPrivate.cols.budget')}</Typography>
                <Typography sx={{ mb: 1.5 }}>{formatBudget(inquiry)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>{t('adminPrivate.cols.source')}</Typography>
                <Typography sx={{ mb: 1.5 }}>{t(`adminPrivate.sources.${inquiry.source}`)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>{t('adminPrivate.fields.commission')}</Typography>
                <Typography sx={{ mb: 1.5 }}>
                  {inquiry.marketplaceCommissionEligible
                    ? t('adminPrivate.commissionEligibleUnset')
                    : t('adminPrivate.commissionBlocked')}
                </Typography>
              </Grid>
            </Grid>

            <Stack spacing={2} sx={{ mt: 2 }}>
              <FormControl fullWidth sx={fieldSx}>
                <InputLabel>{t('adminPrivate.cols.stage')}</InputLabel>
                <Select label={t('adminPrivate.cols.stage')} value={stage} onChange={(e) => setStage(e.target.value)} MenuProps={selectMenuProps}>
                  {INQUIRY_STAGES.map((s) => <MenuItem key={s} value={s}>{t(`adminPrivate.stages.${s}`)}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label={t('adminPrivate.fields.nextAction')} value={nextAction} onChange={(e) => setNextAction(e.target.value)} sx={fieldSx} fullWidth />
              <TextField label={t('adminPrivate.fields.owner')} value={owner} onChange={(e) => setOwner(e.target.value)} sx={fieldSx} fullWidth />
              <TextField label={t('adminPrivate.fields.leadflowId')} value={leadflowLeadId} onChange={(e) => setLeadflowLeadId(e.target.value)} sx={fieldSx} fullWidth helperText={t('adminPrivate.fields.leadflowHint')} />
              <Stack direction="row" flexWrap="wrap" gap={1}>
                <Button
                  disabled={saving}
                  sx={adminSolidBtnSx}
                  onClick={() => patch({
                    stage,
                    nextAction,
                    assignedOwnerLabel: owner,
                    leadflowLeadId: leadflowLeadId === '' ? null : Number(leadflowLeadId),
                  }, 'adminPrivate.saved')}
                >
                  {t('adminPrivate.saveChanges')}
                </Button>
                <Button disabled={saving} sx={adminOutlineBtnSx} onClick={() => patch({ stage: 'qualified', nextAction: 'Request supplier quote' }, 'adminPrivate.qualified')}>
                  {t('adminPrivate.actions.qualify')}
                </Button>
                <Button disabled={saving} sx={adminOutlineBtnSx} onClick={() => patch({ stage: 'quote_requested', nextAction: 'Await supplier quote' }, 'adminPrivate.quoteRequested')}>
                  {t('adminPrivate.actions.requestQuote')}
                </Button>
                <Button
                  disabled={saving}
                  sx={adminSolidBtnSx}
                  onClick={async () => {
                    setSaving(true);
                    setError('');
                    try {
                      const { data } = await api.post('/admin/private/proposals', { inquiryId: Number(inquiryId) });
                      setNotice(t('adminPrivate.proposalCreated'));
                      navigate(`/admin/private/proposals?id=${data.proposal.id}`);
                    } catch (err) {
                      setError(err.response?.data?.error || t('adminPrivate.saveError'));
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  {t('adminPrivate.actions.createProposal')}
                </Button>
                {inquiry.linkedTripId ? (
                  <Button component={RouterLink} to={`/trips/${inquiry.linkedTripId}`} sx={adminOutlineBtnSx}>
                    {t('adminPrivate.actions.openTrip')}
                  </Button>
                ) : null}
              </Stack>
              <Alert severity="info" sx={{ borderRadius: 0 }}>
                {t('adminPrivate.tripLinkHint')}
              </Alert>
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ ...adminSurfaceCardSx, p: 2.5 }} elevation={0}>
            <Typography sx={adminSectionTitleSx}>{t('adminPrivate.activityTitle')}</Typography>
            {activity.length === 0 ? (
              <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13 }}>{t('adminPrivate.noActivity')}</Typography>
            ) : (
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                {activity.map((evt) => (
                  <Box key={evt.id} sx={{ borderBottom: `1px solid ${DOCUMENT.rule}`, pb: 1 }}>
                    <Typography sx={{ fontSize: 13, color: DOCUMENT.ink }}>
                      {t(`adminPrivate.events.${evt.eventType}`, { defaultValue: evt.eventType })}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: DOCUMENT.ink3 }}>
                      {[evt.actorLabel, evt.createdAt].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

function SuppliersPanel({ t }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_SUPPLIER);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/admin/private/suppliers');
      setSuppliers(data.suppliers || []);
    } catch (err) {
      setError(err.response?.data?.error || t('adminPrivate.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const createSupplier = async () => {
    setSaving(true);
    setError('');
    try {
      await api.post('/admin/private/suppliers', draft);
      setDialogOpen(false);
      setDraft(EMPTY_SUPPLIER);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('adminPrivate.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const setVerification = async (id, verificationStatus) => {
    setError('');
    try {
      await api.patch(`/admin/private/suppliers/${id}/verification`, {
        verificationStatus,
        markComplianceChecked: verificationStatus === 'approved',
        permissionToPublish: false,
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('adminPrivate.saveError'));
    }
  };

  return (
    <Box>
      {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }}>{error}</Alert> : null}
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button startIcon={<AddIcon />} sx={adminSolidBtnSx} onClick={() => setDialogOpen(true)}>
          {t('adminPrivate.newSupplier')}
        </Button>
      </Stack>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} sx={{ color: DOCUMENT.oxblood }} />
        </Box>
      ) : suppliers.length === 0 ? (
        <EmptyBlock
          title={t('adminPrivate.emptySuppliersTitle')}
          body={t('adminPrivate.emptySuppliersBody')}
          action={(
            <Button startIcon={<AddIcon />} sx={adminSolidBtnSx} onClick={() => setDialogOpen(true)}>
              {t('adminPrivate.newSupplier')}
            </Button>
          )}
        />
      ) : (
        <TableContainer sx={adminSurfaceCardSx}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('adminPrivate.cols.supplier')}</TableCell>
                <TableCell>{t('adminPrivate.cols.region')}</TableCell>
                <TableCell>{t('adminPrivate.cols.verification')}</TableCell>
                <TableCell>{t('adminPrivate.cols.publish')}</TableCell>
                <TableCell>{t('adminPrivate.cols.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {suppliers.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Typography sx={{ fontSize: 14 }}>{row.businessName}</Typography>
                    <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3 }}>{row.destination || '—'}</Typography>
                  </TableCell>
                  <TableCell>{t(`adminPrivate.regions.${row.destinationRegion}`)}</TableCell>
                  <TableCell>{t(`adminPrivate.supplierStatuses.${row.verificationStatus}`)}</TableCell>
                  <TableCell>
                    {row.permissionToPublish
                      ? t('adminPrivate.publishAllowed')
                      : t('adminPrivate.publishBlocked')}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {row.verificationStatus !== 'in_review' ? (
                        <Button size="small" sx={adminOutlineBtnSx} onClick={() => setVerification(row.id, 'in_review')}>
                          {t('adminPrivate.actions.review')}
                        </Button>
                      ) : null}
                      {row.verificationStatus !== 'approved' ? (
                        <Button size="small" sx={adminOutlineBtnSx} onClick={() => setVerification(row.id, 'approved')}>
                          {t('adminPrivate.actions.approve')}
                        </Button>
                      ) : null}
                      {row.verificationStatus !== 'suspended' ? (
                        <Button size="small" sx={adminOutlineBtnSx} onClick={() => setVerification(row.id, 'suspended')}>
                          {t('adminPrivate.actions.suspend')}
                        </Button>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 0, border: `1px solid ${DOCUMENT.rule}` } }}>
        <DialogTitle sx={{ fontFamily: DOCUMENT.display }}>{t('adminPrivate.newSupplier')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField required label={t('adminPrivate.fields.businessName')} value={draft.businessName} onChange={(e) => setDraft((d) => ({ ...d, businessName: e.target.value }))} sx={fieldSx} fullWidth />
            <TextField label={t('adminPrivate.fields.destination')} value={draft.destination} onChange={(e) => setDraft((d) => ({ ...d, destination: e.target.value }))} sx={fieldSx} fullWidth />
            <FormControl fullWidth sx={fieldSx}>
              <InputLabel>{t('adminPrivate.cols.region')}</InputLabel>
              <Select label={t('adminPrivate.cols.region')} value={draft.destinationRegion} onChange={(e) => setDraft((d) => ({ ...d, destinationRegion: e.target.value }))} MenuProps={selectMenuProps}>
                {REGIONS.map((r) => <MenuItem key={r} value={r}>{t(`adminPrivate.regions.${r}`)}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label={t('adminPrivate.fields.publicUrl')} value={draft.publicUrl} onChange={(e) => setDraft((d) => ({ ...d, publicUrl: e.target.value }))} sx={fieldSx} fullWidth />
            <TextField label={t('adminPrivate.fields.contactName')} value={draft.contactName} onChange={(e) => setDraft((d) => ({ ...d, contactName: e.target.value }))} sx={fieldSx} fullWidth />
            <TextField label={t('adminPrivate.fields.email')} value={draft.contactEmail} onChange={(e) => setDraft((d) => ({ ...d, contactEmail: e.target.value }))} sx={fieldSx} fullWidth />
            <Alert severity="info" sx={{ borderRadius: 0 }}>{t('adminPrivate.supplierPublishHint')}</Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={adminOutlineBtnSx}>{t('common.cancel')}</Button>
          <Button disabled={saving || !draft.businessName.trim()} onClick={createSupplier} sx={adminSolidBtnSx}>
            {saving ? t('adminPrivate.saving') : t('adminPrivate.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function SettingsPanel({ t, onSettingsChanged }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [settings, setSettings] = useState({
    workspaceEnabled: false,
    tagline: '',
    consultationRoutingEmail: '',
    notes: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/admin/private/settings');
      setSettings(data.settings);
    } catch (err) {
      setError(err.response?.data?.error || t('adminPrivate.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const { data } = await api.put('/admin/private/settings', {
        tagline: settings.tagline,
        consultationRoutingEmail: settings.consultationRoutingEmail,
        notes: settings.notes,
      });
      setSettings(data.settings);
      setNotice(t('adminPrivate.settingsSaved'));
      if (onSettingsChanged) onSettingsChanged(data.settings);
    } catch (err) {
      setError(err.response?.data?.error || t('adminPrivate.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} sx={{ color: DOCUMENT.oxblood }} />
      </Box>
    );
  }

  return (
    <Card sx={{ ...adminSurfaceCardSx, p: 2.5, maxWidth: 640 }} elevation={0}>
      {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }}>{error}</Alert> : null}
      {notice ? <Alert severity="success" sx={{ mb: 2, borderRadius: 0 }}>{notice}</Alert> : null}
      <Typography sx={adminSectionTitleSx}>{t('adminPrivate.settingsTitle')}</Typography>
      <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13, mb: 2 }}>{t('adminPrivate.settingsHint')}</Typography>
      <Alert severity="info" sx={{ mb: 2, borderRadius: 0 }}>
        {t('adminPrivate.enableViaFeatures')}{' '}
        <Button component={RouterLink} to="/admin/features" size="small" sx={adminOutlineBtnSx}>
          {t('adminPrivate.openFeatures')}
        </Button>
      </Alert>
      <Stack spacing={2}>
        <TextField
          label={t('adminPrivate.fields.tagline')}
          value={settings.tagline || ''}
          onChange={(e) => setSettings((s) => ({ ...s, tagline: e.target.value }))}
          sx={fieldSx}
          fullWidth
        />
        <TextField
          label={t('adminPrivate.fields.consultationEmail')}
          value={settings.consultationRoutingEmail || ''}
          onChange={(e) => setSettings((s) => ({ ...s, consultationRoutingEmail: e.target.value }))}
          sx={fieldSx}
          fullWidth
        />
        <TextField
          label={t('adminPrivate.fields.notes')}
          value={settings.notes || ''}
          onChange={(e) => setSettings((s) => ({ ...s, notes: e.target.value }))}
          sx={fieldSx}
          fullWidth
          multiline
          minRows={3}
        />
        <Button disabled={saving} onClick={save} sx={adminSolidBtnSx}>
          {saving ? t('adminPrivate.saving') : t('adminPrivate.saveChanges')}
        </Button>
      </Stack>
    </Card>
  );
}

const AdminPrivateTravel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { inquiryId } = useParams();
  const [status, setStatus] = useState(null);
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [gateError, setGateError] = useState('');

  const section = useMemo(() => {
    const path = location.pathname;
    if (path.includes('/inquiries/') && inquiryId) return 'inquiryDetail';
    if (path.startsWith('/admin/private/inquiries')) return 'inquiries';
    if (path.startsWith('/admin/private/proposals')) return 'proposals';
    if (path.startsWith('/admin/private/suppliers')) return 'suppliers';
    if (path.startsWith('/admin/private/partners')) return 'partners';
    if (path.startsWith('/admin/private/performance')) return 'performance';
    if (path.startsWith('/admin/private/settings')) return 'settings';
    return 'overview';
  }, [location.pathname, inquiryId]);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/private/status');
      setStatus(data);
      setGateError('');
    } catch (err) {
      setGateError(err.response?.data?.error || t('adminPrivate.loadError'));
      setStatus({ available: false, enabled: false });
    }
  }, [t]);

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const { data } = await api.get('/admin/private/overview');
      setOverview(data);
      if (data.status) setStatus(data.status);
    } catch (err) {
      // overview readable even when workspace disabled — may 403 on other calls
      if (err.response?.status !== 403) {
        setGateError(err.response?.data?.error || t('adminPrivate.loadError'));
      }
    } finally {
      setOverviewLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (section === 'overview' && status?.available) {
      loadOverview();
    }
  }, [section, status?.available, loadOverview]);

  const openNewOnMount = useMemo(
    () => new URLSearchParams(location.search).get('new') === '1',
    [location.search]
  );

  const workspaceOff = status && status.available && !status.enabled && section !== 'settings';

  return (
    <AdminChrome
      title={t('adminPrivate.title')}
      subtitle={status?.tagline || t('adminPrivate.subtitle')}
      actions={(
        <Stack direction="row" spacing={1}>
          {section === 'inquiries' || section === 'overview' ? (
            <Button
              startIcon={<AddIcon />}
              sx={adminSolidBtnSx}
              onClick={() => navigate('/admin/private/inquiries?new=1')}
              disabled={workspaceOff}
            >
              {t('adminPrivate.newInquiry')}
            </Button>
          ) : null}
        </Stack>
      )}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <DiamondIcon sx={{ fontSize: 18, color: DOCUMENT.accent }} />
        <Typography sx={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
          {t('adminPrivate.eyebrow')}
        </Typography>
      </Box>

      {gateError ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }}>{gateError}</Alert>
      ) : null}

      {status && !status.available ? (
        <EmptyBlock title={t('adminPrivate.envDisabledTitle')} body={t('adminPrivate.envDisabledBody')} />
      ) : (
        <>
          <PrivateSubNav />
          {workspaceOff ? (
            <Alert severity="warning" sx={{ mb: 3, borderRadius: 0 }}>
              {t('adminPrivate.workspaceDisabledBanner')}{' '}
              <Button component={RouterLink} to="/admin/features" size="small" sx={adminOutlineBtnSx}>
                {t('adminPrivate.openFeatures')}
              </Button>
            </Alert>
          ) : null}

          {section === 'overview' ? (
            <OverviewPanel
              overview={overview}
              loading={overviewLoading}
              onRefresh={loadOverview}
              t={t}
              navigate={navigate}
            />
          ) : null}
          {section === 'inquiries' && !workspaceOff ? (
            <InquiriesPanel t={t} navigate={navigate} openNewOnMount={openNewOnMount} />
          ) : null}
          {section === 'inquiryDetail' && !workspaceOff ? (
            <InquiryDetailPanel t={t} navigate={navigate} />
          ) : null}
          {section === 'suppliers' && !workspaceOff ? <SuppliersPanel t={t} /> : null}
          {section === 'proposals' && !workspaceOff ? <PrivateProposalsPanel t={t} /> : null}
          {section === 'partners' && !workspaceOff ? <PrivatePartnersPanel t={t} /> : null}
          {section === 'performance' && !workspaceOff ? <PrivatePerformancePanel t={t} /> : null}
          {section === 'settings' ? (
            <SettingsPanel
              t={t}
              onSettingsChanged={(next) => {
                setStatus((prev) => ({
                  ...(prev || {}),
                  available: true,
                  enabled: Boolean(next.workspaceEnabled),
                  tagline: next.tagline,
                }));
                if (next.workspaceEnabled) loadOverview();
              }}
            />
          ) : null}
        </>
      )}
    </AdminChrome>
  );
};

export default AdminPrivateTravel;
